import { useEffect, useState } from "react";
import { Link, redirect, useFetcher, useLocation, useNavigate, useNavigation } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { useSuspenseQueries } from "@tanstack/react-query";
import type { HomeFeedItem, HomeFeedPage } from "@repo/shared-types";
import { api } from "@/lib/api";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { loadSession } from "@/lib/session";
import { useSession } from "@/routes/root";
import { viewerZone } from "@/lib/times";
import { isGeneratedUsername } from "@/lib/usernames";
import { AtSign, CalendarDays, Eye, TriangleAlert } from "lucide-react";
import { EmptyNote } from "@/components/empty-note";
import { EventCard } from "@/components/event-card";
import { PageTitle, Wordmark } from "@/components/brand";
import { InstallPrompt } from "@/components/install-prompt";
import { TopBar, useAccountLink } from "@/components/navigation";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";

/**
 * Home: what every space you are in has published, the latest first, each
 * event under its space's name (decided with Tiago 10 October). Published
 * alone: drafts and scheduled events stay on their space's own feed, for
 * whoever may see them there.
 *
 * Signed out there is nothing to show, so it is the sign-in page.
 *
 * Following (decided with Tiago 20 October) is Home's other view: the
 * events the viewer follows, the last followed first, in the same cards. An
 * eye beside the wordmark switches to it, pressed while it shows (0077), and
 * the view is in the address — so Back comes back to it — while the tab
 * bar's Home is `/`, which always opens on All.
 *
 * "Show more" reads the next page through this route's own loader, by
 * fetcher, and keeps what it read below the first page until the view
 * changes. The first page is a query (0075): drawn from its copy, read again.
 */

type View = "all" | "following";
/** What the loader answers "Show more" with; a page's own read is a query. */
type MoreData = { page: HomeFeedPage };

function viewOf(search: URLSearchParams): View {
  return search.get("view") === "following" ? "following" : "all";
}

export async function homeLoader({ request }: LoaderFunctionArgs) {
  const query = new URL(request.url).searchParams;
  const view = viewOf(query);
  const cursor = query.get("cursor");
  if (cursor) return { page: await api.feed(cursor, view) } satisfies MoreData;

  if (!(await loadSession())) throw redirect("/sign-in");
  await Promise.all([read(queries.feed(view)), read(queries.profile())]);
  return null;
}

export default function Home() {
  const session = useSession();
  const view = viewOf(new URLSearchParams(useLocation().search));
  const [{ data: page }, { data: profile }] = useSuspenseQueries({
    queries: [queries.feed(view), queries.profile()],
  });
  const zone = viewerZone(profile.timeZone).zone;
  const navigate = useNavigate();
  const navigation = useNavigation();
  // The view tapped shows at once, while its events are on their way.
  const going = navigation.location?.pathname === "/" ? viewOf(new URLSearchParams(navigation.location.search)) : null;
  const chooseUsername = useAccountLink("profile");
  // The loader let the page draw only with a session.
  if (!session) return null;
  const inASpace = session.memberships.some((m) => !m.tenant.isPersonal);

  return (
    <section className="space-y-6">
      {/* The top of the app wears its name, as a phone app's home does; the
          page's heading stays for a screen reader. Nothing to follow outside
          a space: the eye waits until there is. */}
      <TopBar>
        <Wordmark title />
        {inASpace && (
          // Outlined like the cog in another page's bar, and filled like a followed eye while pressed.
          <Toggle
            variant="outline"
            size="lg"
            aria-label="Following"
            className="size-10 rounded-full border-border px-0 aria-pressed:border-transparent aria-pressed:bg-primary aria-pressed:text-primary-foreground aria-pressed:hover:bg-primary/80 aria-pressed:hover:text-primary-foreground"
            pressed={(going ?? view) === "following"}
            onPressedChange={(on) =>
              navigate(on ? "/?view=following" : "/", { replace: true, preventScrollReset: true })
            }
          >
            <Eye aria-hidden="true" />
          </Toggle>
        )}
      </TopBar>
      <PageTitle of={["Home"]} />
      <h1 className="sr-only">Home</h1>

      <InstallPrompt />

      {!session.user.emailVerified && (
        <Alert role="note" className="border-transparent bg-warning-muted text-warning">
          <TriangleAlert />
          <AlertDescription className="text-foreground">Your email address is not verified.</AlertDescription>
        </Alert>
      )}

      {/* Sign-up never stops to ask for a handle, so it hands out a random
          one. This is where somebody finds out they can do better. */}
      {isGeneratedUsername(session.user.username) && (
        <Alert role="note" className="border-transparent bg-muted">
          <AtSign />
          <AlertDescription>
            <p>
              People find you by your username, and yours is still the random{" "}
              <strong className="font-medium text-foreground">@{session.user.username}</strong> you were given at
              sign-up.{" "}
              <Link to={chooseUsername} className="font-medium text-foreground underline underline-offset-4">
                Choose your own
              </Link>
            </p>
          </AlertDescription>
        </Alert>
      )}

      <Feed key={view} page={page} view={view} zone={zone} inASpace={inASpace} />
    </section>
  );
}

/** One view's events, and the pages "Show more" read past its first. */
function Feed({ page, view, zone, inASpace }: { page: HomeFeedPage; view: View; zone: string; inASpace: boolean }) {
  const more = useFetcher<MoreData>();
  const [extra, setExtra] = useState<HomeFeedItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const now = Date.now();

  useEffect(() => {
    if (!more.data) return;
    const loaded = more.data.page;
    setExtra((shown) => [...shown, ...loaded.items]);
    setCursor(loaded.nextCursor ?? "");
  }, [more.data]);

  // Until a later page is read, the first page says whether there is one.
  const next = cursor === null ? page.nextCursor : cursor || null;
  const items = unique([...page.items, ...extra]);

  return (
    <>
      {items.length === 0 ? (
        <EmptyNote
          icon={view === "following" ? Eye : CalendarDays}
          action={
            !inASpace && (
              <Button asChild size="lg">
                <Link to="/spaces">Find or create a space</Link>
              </Button>
            )
          }
        >
          {view === "following" ? (
            <p>Events you follow show up here. Follow one from the … on its card.</p>
          ) : inASpace ? (
            <p>Nothing posted in your spaces yet.</p>
          ) : (
            <p>Events from your spaces show up here. Join or create one to get started.</p>
          )}
        </EmptyNote>
      ) : (
        <ul className="space-y-3">
          {items.map(({ space, event, canManageEvents }) => (
            <li key={event.id}>
              <EventCard
                event={event}
                base={`/t/${space.slug}`}
                zone={zone}
                space={space}
                canManage={canManageEvents}
                now={now}
              />
            </li>
          ))}
        </ul>
      )}

      {next && (
        <div className="text-center">
          <Button
            type="button"
            variant="outline"
            size="lg"
            disabled={more.state !== "idle"}
            onClick={() =>
              more.load(
                `/?index${view === "following" ? "&view=following" : ""}&cursor=${encodeURIComponent(next)}`,
              )
            }
          >
            Show more
          </Button>
        </div>
      )}
    </>
  );
}

/** Once each: the first page read again can hold what a later one did. */
function unique(items: HomeFeedItem[]): HomeFeedItem[] {
  const seen = new Set<string>();
  return items.filter(({ event }) => !seen.has(event.id) && seen.add(event.id));
}
